import { FC } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { ConfirmActionModal } from "src/components/Modal";
import { mapToModalError, useCreateError } from "src/errors";
import { useCreateScenarioDraftVersionMutation } from "src/graphql/anti-fraud/queries/scenarios/CreateScenarioDraftVersionMutation.ts";
import { getFullScenarioQuery } from "src/graphql/anti-fraud/queries/scenarios/GetFullScenarioQuery.ts";
import { useGetShortScenarioLazyQuery } from "src/graphql/anti-fraud/queries/scenarios/GetShortScenarioQuery.ts";
import { useUpdateScenarioDraftVersionMutation } from "src/graphql/anti-fraud/queries/scenarios/UpdateScenarioDraftVersionMutation.ts";
import { useT } from "src/internationalization";
import { ShortScenario } from "src/types/scenario.ts";

import { CreateVersionData } from "./CreateVersion.types.ts";

export type ConfirmScenarioVersionCreationModalProps = {
  scenario: ShortScenario;
  versionData: CreateVersionData;
  onClose: () => void;
  onSubmit: () => void;
};

export const ConfirmScenarioVersionCreationModal: FC<ConfirmScenarioVersionCreationModalProps> = ({
  scenario,
  versionData,
  onClose,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const t = useT();
  const createError = useCreateError();

  const [createVersion, { loading: createVersionLoading, error: createVersionError }] =
    useCreateScenarioDraftVersionMutation({
      refetchQueries: [{ query: getFullScenarioQuery, variables: { id: scenario.id } }],
      awaitRefetchQueries: true,
    });

  const [updateVersion, { loading: updateVersionLoading, error: updateVersionError }] =
    useUpdateScenarioDraftVersionMutation({
      refetchQueries: [{ query: getFullScenarioQuery, variables: { id: scenario.id } }],
      awaitRefetchQueries: true,
    });

  const [fetchScenario, { loading: fetchScenarioLoading }] = useGetShortScenarioLazyQuery();

  const handleSubmit = async () => {
    try {
      const { data } = await createVersion({
        variables: {
          scenarioId: scenario.id,
        },
      });

      if (!data) return;

      await updateVersion({
        variables: {
          id: data.createDraftVersion.id,
          options: {
            name: versionData.name,
            description: versionData.description,
            rules: versionData.rules.map((r) => ({
              ruleId: r.id,
              score: r.score,
            })),
            threshold: versionData.threshold,
          },
        },
      });

      // Fetch one scenario to update the cache, instead of refetching all scenarios
      fetchScenario({
        variables: {
          id: scenario.id,
        },
        // Even if an error happens then just ignore the error and proceed with the onSubmit
      }).finally(() => {
        onSubmit();
        navigate(
          generatePath("/scenario/:caseTypeCode/:id/draft", {
            caseTypeCode: scenario.caseType.code,
            id: scenario.id,
          })
        );
      });
    } catch {
      // Ignore error
    }
  };

  const error =
    createVersionError || updateVersionError
      ? createError("SERVER-ERROR", createVersionError ?? updateVersionError)
      : null;

  return (
    <ConfirmActionModal
      title={t("create.scenario.version.confirmation.modal.title")}
      subtitle={scenario.name}
      error={mapToModalError(error)}
      confirmButtonText={t("general.actions.confirm")}
      loading={createVersionLoading || updateVersionLoading || fetchScenarioLoading}
      onConfirm={handleSubmit}
      size="sm"
      onClose={onClose}
    >
      {t("create.scenario.version.confirmation.modal.message")}
    </ConfirmActionModal>
  );
};
